import { TaskEditDialogComponent } from '../task-edit-dialog/task-edit-dialog.component';
import { LazyLoadEvent, MenuItem, ConfirmationService, SelectItem } from 'primeng/api';
import { TaskDto } from 'src/app/tasks/shared/dto/task.dto';
import { TaskService } from '../shared/task.service';
import { TaskSearchDto } from '../shared/dto/task-search.dto';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { PagedResultGeneric, DatagridService, TranslationService, NotificationService } from 'src/app/shared';
import { timeoutWrapper } from 'src/app/shared/decorator/timeout-wrapper';
import { UserService } from 'src/app/users/shared/user.service';
import { SecurityAccessLevelEnum } from 'src/app/shared/auth/enum/security-access-level.enum';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';

@Component({
  selector: 'wl-task-search-grid',
  templateUrl: './task-search-grid.component.html',
  styleUrls: ['./task-search-grid.component.less'],
  standalone: false,
})
export class TaskSearchGridComponent implements OnInit {
  @ViewChild(TaskEditDialogComponent) _taskEditDialog: TaskEditDialogComponent;
  @Input() searchParams: TaskSearchDto;

  @Output() completedSearch = new EventEmitter<PagedResultGeneric<TaskDto>>();
  taskSearchResult: PagedResultGeneric<TaskDto>;
  loading = false;
  rowMenuItems = new Map<number, MenuItem[]>();
  pagingSizeItems: SelectItem[] = [];
  fullLazyLoad = false;
  profileModuleFullAccess: boolean = false;
  profileTasksTabFullAccess: boolean = false;
  exporting = false;
  exportDisabled = true;

  constructor(
    private _taskService: TaskService,
    private _datagridService: DatagridService,
    private _translationService: TranslationService,
    private _notificationService: NotificationService,
    private _confirmationService: ConfirmationService,
    private _userService: UserService,
    private _navigationService: NavigationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.pagingSizeItems = [
      {
        label: await this._translationService.getCapitalizeFirstCharacter('tasks.search.show-20'),
        value: 20,
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('tasks.search.show-10'),
        value: 10,
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('tasks.search.show-all'),
        value: 0,
      },
    ];

    this._userService.getCurrentUserSecurityElementAccessLevel('Profiles_Module').then((userAccessLevel: number) => {
      this.profileModuleFullAccess = userAccessLevel > SecurityAccessLevelEnum.ReadOnlyAccess;

      if (this.profileModuleFullAccess) {
        this._userService.getCurrentUserSecurityElementAccessLevel('ProfileTasks_Tab').then((userAccessLevel: number) => {
          this.profileTasksTabFullAccess = userAccessLevel > SecurityAccessLevelEnum.ReadOnlyAccess;
        });
      }
    });
  }

  @timeoutWrapper()
  lazyLoadTasks(event: LazyLoadEvent) {
    if (!this.fullLazyLoad) {
      // we got the data from local storage. don't load data from the api.
      this.fullLazyLoad = true;
      return;
    }

    this._datagridService.mapDatagridPagingToPagedSearch(event, this.searchParams);
    this.search();
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  public search() {
    this.loading = true;
    this.searchParams.IncludeProfile = true;
    this.fullLazyLoad = true;
    this._taskService
      .search(this.searchParams)
      .then((result) => {
        this.taskSearchResult = result;
        this.searchParams.TotalCount = result.TotalCount;

        this.exportDisabled = result.TotalCount == 0;

        this.completedSearch.emit(result);
      })
      .finally(() => (this.loading = false));
  }

  actionItemsForTask(task: TaskDto): MenuItem[] {
    if (!this.rowMenuItems.has(task.TaskId)) {
      this.rowMenuItems.set(task.TaskId, [
        {
          label: this._translationService.getInstantCapitalizeFirstCharacter('tasks.search.edit'),
          visible: this.profileTasksTabFullAccess,
          command: () => this.launchEditDialog(task.TaskId),
        },
        {
          label: this._translationService.getInstantCapitalizeFirstCharacter('tasks.search.delete'),
          visible: this.profileTasksTabFullAccess,
          command: () => this.deleteTask(task.TaskId),
        },
      ]);
    }
    return this.rowMenuItems.get(task.TaskId);
  }

  launchEditDialog(taskId: number) {
    this._taskEditDialog?.open(taskId);
  }

  closedChanged(task: TaskDto) {
    if (task.Closed) {
      task.DateClosed = new Date();
    }
    this.updateTask(task);
  }

  updateTask(task: TaskDto) {
    this._taskService.update(task).then(
      () => {
        this._notificationService.translateSuccess('tasks.search.updated-task-message');
      },
      (error) => {
        this._notificationService.httpError(error);
      }
    );
  }

  deleteTask(taskId: number) {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('tasks.search.delete-confirmation-message'),
      accept: () => {
        this.loading = true;
        this._taskService.delete(taskId).then(
          () => {
            this._notificationService.translateSuccess('tasks.search.task-deleted');
            this.search();
          },
          (error) => {
            this._notificationService.httpError(error);
          }
        );
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  exportTasks() {
    this.exporting = true;
    this.exportDisabled = true;
    this._taskService
      .exportTasks(this.searchParams)
      .then((result) => {
        this._navigationService.popupSafeNavigate(result);
      })
      .finally(() => {
        this.exporting = false;
        this.exportDisabled = false;
      });
  }
}
